import React, { useState, useRef, useEffect } from 'react';

/**
 * CurrencyInput BRL - Implementação manual robusta
 * Aceita vírgula como separador decimal, ponto como milhar
 * Retorna valor numérico via onValueChange
 */
const CurrencyInput = ({ 
  value, 
  onValueChange, 
  placeholder = "0,00",
  className = "",
  id,
  disabled = false,
  ...props 
}) => {
  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [rawInput, setRawInput] = useState('');

  // Formata número para exibição pt-BR
  const formatForDisplay = (num) => {
    if (num === 0 || num === undefined || num === null) return '';
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Formata texto digitado em tempo real com milhar + vírgula
  const formatInput = (text) => {
    // Remove tudo exceto dígitos e vírgula
    let cleaned = text.replace(/[^\d,]/g, '');
    // Garante apenas uma vírgula
    const parts = cleaned.split(',');
    if (parts.length > 2) {
      cleaned = parts[0] + ',' + parts.slice(1).join('');
    }
    // Limita 2 casas decimais
    const [intPart, decPart] = cleaned.split(',');
    const trimmedDec = decPart !== undefined ? decPart.slice(0, 2) : undefined;
    // Adiciona pontos de milhar na parte inteira
    const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return trimmedDec !== undefined ? intFormatted + ',' + trimmedDec : intFormatted;
  };

  // Parseia texto pt-BR para número
  const parseToNumber = (text) => {
    if (!text) return 0;
    // Remove pontos de milhar, troca vírgula por ponto
    const num = parseFloat(text.replace(/\./g, '').replace(',', '.'));
    return isNaN(num) ? 0 : num;
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Ao focar, mostra o valor atual formatado para edição
    if (value && value > 0) {
      setRawInput(formatForDisplay(value));
    } else {
      setRawInput('');
    }
  };

  const handleChange = (e) => {
    const formatted = formatInput(e.target.value);
    setRawInput(formatted);
    // Atualiza o valor em tempo real
    const num = parseToNumber(formatted);
    onValueChange(num);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const num = parseToNumber(rawInput);
    onValueChange(num);
    setRawInput('');
  };

  // Valor exibido: durante edição mostra rawInput, senão mostra formatado com prefixo
  const displayValue = isFocused 
    ? rawInput 
    : (value && value > 0 ? 'R$ ' + formatForDisplay(value) : '');

  return (
    <input
      ref={inputRef}
      id={id}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={`flex w-full rounded-md border border-input bg-transparent px-3 py-1 shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm ${className}`}
      {...props}
    />
  );
};

export default CurrencyInput;
